import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MailCheck, RefreshCw, Copy } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const TONE = { verified: 'bg-emerald-100 text-emerald-800', pending: 'bg-amber-100 text-amber-800', not_started: 'bg-stone-100 text-stone-700', failed: 'bg-red-100 text-red-700' };

const RecordsTable = ({ records }) => (
  <div className="overflow-x-auto rounded-xl border border-stone-200" data-testid="email-domain-records">
    <table className="w-full text-xs">
      <thead className="bg-stone-50 text-stone-500"><tr><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Value</th><th className="text-left px-3 py-2">Status</th></tr></thead>
      <tbody>
        {records.map((r, i) => (
          <tr key={i} className="border-t border-stone-100 align-top">
            <td className="px-3 py-2 font-mono">{r.type || r.record}{r.priority != null ? ` (${r.priority})` : ''}</td>
            <td className="px-3 py-2 font-mono break-all">{r.name}</td>
            <td className="px-3 py-2 font-mono break-all max-w-md">
              {r.value}
              <button type="button" className="ml-1 text-stone-400 hover:text-stone-800" onClick={() => { navigator.clipboard.writeText(r.value); toast.success('Copied'); }}><Copy size={11} /></button>
            </td>
            <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${TONE[r.status] || TONE.not_started}`}>{r.status || 'pending'}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const EmailDomainCard = () => {
  const [d, setD] = useState(null);
  const [domain, setDomain] = useState('its-raw.com');
  const [local, setLocal] = useState('billing');
  const [busy, setBusy] = useState('');
  const load = () => axios.get(`${API}/admin/email-domain`, { headers: headers() }).then((r) => setD(r.data || {})).catch(() => setD({}));
  useEffect(() => { load(); }, []);
  const call = async (kind) => {
    setBusy(kind);
    try {
      const r = kind === 'add'
        ? await axios.post(`${API}/admin/email-domain`, { domain, from_local: local, from_name: 'RAW' }, { headers: headers() })
        : await axios.post(`${API}/admin/email-domain/verify`, {}, { headers: headers() });
      setD(r.data);
      toast.success(r.data.verified ? 'Domain verified — emails now send from your domain' : kind === 'add' ? 'Domain added — add the DNS records below' : `Status: ${r.data.status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setBusy('');
    }
  };
  if (!d) return <div className="h-32 rounded-2xl bg-stone-100 animate-pulse" />;
  return (
    <div className="lg-glass rounded-2xl border p-5 space-y-4" data-testid="email-domain-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-stone-900 flex items-center gap-2"><MailCheck size={18} /> Sender domain</h3>
        {d.domain && <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${d.verified ? TONE.verified : TONE.pending}`} data-testid="email-domain-status">{d.verified ? 'Verified' : d.status || 'pending'}</span>}
      </div>
      <p className="text-sm text-stone-600">
        {d.verified ? <>All receipts, invoices and notices now send from <b data-testid="email-domain-sender">{d.address}</b>.</>
          : 'Verify your own domain so receipts, invoices and cancellation emails reach every client instead of spam.'}
      </p>
      {!d.domain && (
        <div className="flex flex-wrap gap-2 items-center">
          <Input className="w-32" value={local} onChange={(e) => setLocal(e.target.value)} data-testid="email-domain-local-input" />
          <span className="text-stone-500">@</span>
          <Input className="w-56" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="email-domain-input" />
          <Button onClick={() => call('add')} disabled={!!busy} data-testid="email-domain-add-btn">{busy === 'add' ? 'Adding…' : 'Add domain'}</Button>
        </div>
      )}
      {d.domain && !d.verified && (
        <>
          <p className="text-xs text-stone-500">Add these records at your domain registrar (GoDaddy, Cloudflare, etc.) for <b>{d.domain}</b>, then click Check verification. DNS can take up to a few hours.</p>
          <RecordsTable records={d.records || []} />
          <Button variant="outline" onClick={() => call('verify')} disabled={!!busy} data-testid="email-domain-verify-btn">
            <RefreshCw size={14} className={`mr-1 ${busy === 'verify' ? 'animate-spin' : ''}`} /> Check verification
          </Button>
        </>
      )}
      {d.error && <p className="text-xs text-red-600" data-testid="email-domain-error">{d.error}</p>}
    </div>
  );
};
